import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpEventType,
  HttpProgressEvent,
  HttpParams,
  HttpResponse,
  HttpStatusCode
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SKIP_ERROR_TOAST, SKIP_LOADER_HIDE } from '../../../interceptors/error.interceptor';
import { isPseToCorrect, Task, TaskAssignResponse, TaskCopyError, TaskForm, TaskFormValues, TaskMultiAcceptResponse, TaskStatus, TaskStatusChangeResponse } from '../model/task.model';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatAll,
  concatMap,
  defaultIfEmpty,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  from,
  last,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  Subject,
  race,
  switchMap,
  take,
  tap,
  timer,
  throwError,
  withLatestFrom
} from 'rxjs';
import { API_URL } from 'src/app/config/general.config';
import { OfflineService } from 'src/app/services/offline.service';
import {
  Control,
  FormEntry,
  FormEntryValue,
  FormValue,
  getFormEntryInstanceKey
} from '../model/form.model';
import { ConnectionStatusService } from 'src/app/services/connection-status.service';
import { AttachmentService } from 'src/app/services/attachment.service';
import { Attachment, PhotoAttachment } from 'src/app/model/file.model';
import { AttachmentEntity, TaskDataEntity } from 'src/app/config/dbstorage.config';
import { StoreService } from 'src/app/services/store.service';
import { TranslateService } from '@ngx-translate/core';
import { TASK_SEARCH_MODE, TaskSearchMode } from '../config/task.config';
import { Router } from '@angular/router';
import {DeviceDetectorService} from "ngx-device-detector";
import { SyncMode, SyncPhotoIssue, SyncPhotoIssueReason, SyncProgressService } from './sync-progress.service';

interface PhotoSyncCandidate {
  photo: PhotoAttachment;
  entryId: number;
  cloneIndex: number;
  entryPath: string[];
  source: 'FAILURE' | 'PHOTO_CONTROL';
  failureName?: string;
  controlId?: number;
  controlLabel?: string;
}

class AttachmentSyncError extends Error {
  constructor(readonly photoIssues: SyncPhotoIssue[]) {
    super('SYNC.ATTACHMENTS_INCOMPLETE');
    this.name = 'AttachmentSyncError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class TasksService {
  private readonly syncRetryDelayMs = 10000;
  private readonly syncRetryMaxAttempts = 5;
  private readonly stopSync$ = new Subject<void>();
  private readonly activeSyncTaskIds = new Set<number>();
  private readonly syncableOnlineTaskStatuses: TaskStatus[] = ['DRAFT', 'IN_PROGRESS'];

  private _tasksApi(): string {
    return API_URL + '/tasks';
  }
  private _projectsApi(projectId: string): string {
    return API_URL + '/projects/' + projectId + '/tasks';
  }

  private _taskIdApi(taskId: number): string {
    return this._tasksApi() + '/' + taskId;
  }

  private _tasks$ = new BehaviorSubject<Task[]>([]);
  private tasksListMode$ = new BehaviorSubject<TaskSearchMode>(this.defaultTaskMode());
  private refreshTasks$ = new BehaviorSubject<void>(undefined);
  tasks$ = merge(
    this._tasks$.pipe(
      withLatestFrom(this.tasksListMode$),
      map(([tasks, mode]) => tasks.filter(t => {
        switch(mode) {
          case TASK_SEARCH_MODE.MY:
          case TASK_SEARCH_MODE.ACTIVE:
            return t.status === 'DRAFT' || t.status === 'IN_PROGRESS';
          case TASK_SEARCH_MODE.ARCHIVED:
            return t.status === 'CLOSED';
          case TASK_SEARCH_MODE.TO_ACCEPT:
            return t.status === 'TO_ACCEPT';
          default:
            return false;
        }
      }))
    ),
    combineLatest([
      this.tasksListMode$,
      this._storeService.projectChanged$,
      this.refreshTasks$
    ]).pipe(
      tap(([_, project]) => {
        if (!project || !this._storeService.canSeeTasks()) {
          void this.router.navigate(['']);
        }
      }),
      switchMap(([mode, project]) => {
        this._storeService.showLoader();
        switch (mode) {
          case TASK_SEARCH_MODE.MY:
            return this.getMyTasks(project!.id);
          case TASK_SEARCH_MODE.ACTIVE:
            return this.getCurrentTasks(project!.id);
          case TASK_SEARCH_MODE.ARCHIVED:
            return this.getArchivedTasks(project!.id);
          case TASK_SEARCH_MODE.TO_ACCEPT:
            return this.getTaskToAccept(project!.id);
          default:
            return of([]);
        }
      }),
      tap(() => this._storeService.hideLoader()),
    )
  ).pipe(
    tap(() => this._storeService.updateActionsCount()),
    shareReplay({refCount: false, bufferSize: 1}),
    switchMap(tasks => this.determineOfflineStatus(tasks)),
  )

  isMobile = this.deviceService.isMobile();

  constructor(
    private _http: HttpClient,
    private _attachmentService: AttachmentService,
    private _offlineService: OfflineService,
    private _connectionStatusService: ConnectionStatusService,
    private _storeService: StoreService,
    private _translateService: TranslateService,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private syncProgressService: SyncProgressService
  ) {
    this.tasks$.subscribe(tasks => {
      console.log('Current tasks:', tasks);
    });
  }

  refreshTasks(): void {
    this.refreshTasks$.next();
  }

  public determineOfflineStatus(tasks: Task[]): Observable<Task[]> {
    if(!tasks.length){
      return of([]);
    }
    return forkJoin({
      taskData: this._storeService.projectChanged$.pipe(
        take(1),
        switchMap(project => this._offlineService.fetchAllTaskData(project?.id as string)),
        defaultIfEmpty([]),
        map(atts => atts.map(att => att.taskId))
      ),
      attsData: this._offlineService.fetchAllAttachments().pipe(
        defaultIfEmpty([]),
        map(atts => atts.map(att => att.taskId))
      )
    }).pipe(
      map(({taskData, attsData}) => {
        const dataIds = new Set([...taskData, ...attsData]);
        const toRemoveIds = new Set<number>();
        tasks.forEach(task => {
          if(dataIds.has(String(task.id)) && task.assignedTo?.id === this._storeService.loggedUser.id) {
            task.localVersion = true;
          }
          else if (dataIds.has(String(task.id))) {
            toRemoveIds.add(task.id);
          }
        })
        const toRemove = [...toRemoveIds].map(id =>
          this.cleanupTaskData(id)
        );

        forkJoin(toRemove).subscribe();
        return tasks;
      }),
      catchError(error => {
        console.error('[TasksService] Failed to determine offline task status. Falling back to tasks without local markers.', error);
        return of(tasks.map(task => ({ ...task, localVersion: false })));
      })
    )
  }

  setTasksListMode(mode: TaskSearchMode): void {
    this.tasksListMode$.next(mode);
  }

  getTasksListMode() {
    return this.tasksListMode$.asObservable();
  }

  defaultTaskMode(): TaskSearchMode {
    if ((this._storeService.hasTaskApproveRole() || this._storeService.hasTaskEditRole()) && !this._storeService.hasTaskReceiveRole()) {
      return TASK_SEARCH_MODE.TO_ACCEPT;
    }
    if (this._storeService.hasTaskReceiveRole()) {
      return TASK_SEARCH_MODE.MY;
    }
    return TASK_SEARCH_MODE.ACTIVE
  }

  getTaskToAccept(projectId: string): Observable<Task[]>{
    return this._http.get<Task[]>(`${this._projectsApi(projectId)}/toaccept`, { params: this.getTasksParams() }).pipe(
      switchMap(tasks => this.storeTaskForms(projectId, tasks))
    );
  }

  getCurrentTasks(projectId: string): Observable<Task[]>{
    return this._http.get<Task[]>(`${this._projectsApi(projectId)}/current`, { params: this.getTasksParams() }).pipe(
      switchMap(tasks => this.storeTaskForms(projectId, tasks))
    );
  }

  getMyTasks(projectId: string): Observable<Task[]>{
    const withFormData = this.isMobile;
    return this._http.get<Task[]>(`${this._projectsApi(projectId)}/my`, { params: this.getTasksParams(true) }).pipe(
      map(tasks => tasks.map(t => {
        if(t.form && typeof(t.form) === "string") {
          const form = JSON.parse(t.form) as TaskForm;
          this.mapActualFormValues(form, t.formValues);
          t.form = form;
          delete t.formValues;
        }
        return t;
      })),
      switchMap(tasks => this.storeMyTasks(projectId, tasks, withFormData)),
      switchMap(tasks => this.storeTaskForms(projectId, tasks))
    );
  }

  private storeMyTasks(projectId: string, tasks: Task[], withFormData: boolean): Observable<Task[]> {
    if (!withFormData || !tasks.length) {
      return of(tasks);
    }

    return forkJoin(
      tasks.map(task => this._offlineService.hasOfflineData(task.id).pipe(
        switchMap(hasOfflineData => hasOfflineData
          ? of(task)
          : this._offlineService.updateTask(task, projectId)
        )
      ))
    ).pipe(map(() => tasks));
  }

  getArchivedTasks(projectId: string): Observable<Task[]>{
    return this._http.get<Task[]>(`${this._projectsApi(projectId)}/closed`, { params: this.getTasksParams() }).pipe(
      switchMap(tasks => this.storeTaskForms(projectId, tasks))
    );
  }

  private storeTaskForms(projectId: string, tasks: Task[]): Observable<Task[]> {
    const forms = (tasks ?? [])
      .filter(task => task.form != null)
      .map(task => this.prepareTemplateForm(task.form!))
      .map(form => this._offlineService.updateForm(projectId, form));
    return forms.length ? forkJoin(forms).pipe(map(() => tasks)) : of(tasks);
  }

  private prepareTemplateForm(form: TaskForm) {

    const clearEntryValues = (entry: FormEntry) => {
      entry.failureDescription = [];
      entry.control?.forEach(control => {
        control.value = null;
        control.controls?.forEach(subControl => subControl.value = null);
      })
      entry.children?.forEach(clearEntryValues);
    }

    const templateForm = JSON.parse(JSON.stringify(form)) as TaskForm;
    templateForm.formHeaders = [];
    templateForm.entries.forEach(clearEntryValues);
    return templateForm;
  }
  getTask(projectId: string, taskId: number): Observable<Task> {
    return this.tasks$.pipe(
      withLatestFrom(this._connectionStatusService.networkStatus$),
      switchMap(([tasks, isOnline]) => {
        let cachedTask: Task | undefined;
        if(!isOnline) {
          //Jeśli jesteśmy offline, to szukamy taska na skeszowanej liście i jego użyjemy.
          // W przeciwnym razie pobierzemy aktualnego taska z serwera
          cachedTask = tasks.find(task => String(task.id) === String(taskId));
        }
        return this.getOfflineTask(projectId, taskId, cachedTask)
      }),
      tap(task => {
        if (task.form?.entries) {
          this.propagateCustomValuesToChildren(task.form.entries);
        }
      })
    )

  }

  private propagateCustomValuesToChildren(entries: FormEntry[], customLabel?: string, customValue?: string): void {
    entries.forEach(entry => {
      entry.customLabel = entry.customLabel ?? customLabel;
      entry.customValue = entry.customValue ?? customValue;
      if (entry.children?.length) {
        this.propagateCustomValuesToChildren(entry.children, entry.customLabel, entry.customValue);
      }
    });
  }
  private getOfflineTask(projectId: string, taskId: number, cachedTask?: Task): Observable<Task>{
    return this._offlineService.getTask(projectId, taskId).pipe(
      switchMap(task => {
        if(task) {
          return of(task);
        }
        if(cachedTask) {
          return of({...cachedTask}).pipe(
            map(task => { task.localVersion = true; return task }),
            switchMap(task => this.mergeTaskForm(projectId, task)),
            switchMap(task => this._offlineService.updateTask(task, projectId))
          )
        }
        return this._http.get<Task>(this._taskIdApi(taskId)).pipe(
          switchMap(task => this._offlineService.updateTask(task, projectId))
        );
      })
    )
  }

  private mergeTaskForm(projectId: string, task: Task): Observable<Task> {
    if(task.form) {
      return of(task);
    }
    return this._offlineService.fetchForm(projectId, task.formValues!.templateFormId, task.formValues!.templateFormVersion)
    .pipe(
      map(form => {
        task.form = JSON.parse(JSON.stringify(form?.payload));
        this.mapActualFormValues(task.form, task.formValues)
        return task;
      })
    )
  }

  private mapActualFormValues(form?: TaskForm, formValues?: TaskFormValues): void {
    if(!form || !formValues) {
      return;
    }
    const values = new Map<string, FormEntry>();
    const valuesToMap = (value: FormEntry) => {
      values.set(getFormEntryInstanceKey(value.templateFormEntryId, value.cloneIndex), value)
      value.children?.forEach(child => valuesToMap(child))
    }
    const updateControlValue = (control: Control, controlValue?: Control) => {
      control.value = controlValue?.value;
      control.controls?.forEach(subControl => subControl.value = controlValue?.controls?.find(sc => subControl.templateFormControlId === sc.templateFormControlId)?.value)
    }
    const mapFormValues = (entry: FormEntry) => {

      const entryValue = values.get(getFormEntryInstanceKey(entry.templateFormEntryId, entry.cloneIndex));
      if(!entryValue){
        return;
      }
      entry.failureDescription = entryValue.failureDescription ?? [];
      entry.control?.forEach(control => updateControlValue(control, entryValue.controlValues?.find(c => control.templateFormControlId === c.templateFormControlId)));
      entry.children?.forEach(mapFormValues);
    }
    formValues.entryValues?.forEach(entry => valuesToMap(entry));
    form.entries.forEach(mapFormValues);
    form.formHeaders = formValues.formHeaders;
  }

  private updateTasksInList(tasks: Task[], currentTasks: Task[]) {
    const updatedTasks = tasks.reduce((acc, task) => {acc[task.id] = task; return acc;}, {} as Record<number, Task>);
    this._tasks$.next(currentTasks.map(t => updatedTasks[t.id] ?? t));
  }

  saveTaskOnline(projectId: string, taskId: number, formEntries: FormValue, isDraft: boolean, context?: HttpContext): Observable<Task> {
    return this._connectionStatusService.networkStatus$
    .pipe(
      take(1),
      switchMap(isOnline => {
        if(isOnline) {
          const { blocked, ...body } = formEntries;
          const params = this.getBlockedParams(blocked);
          return isDraft
          ? this._http.put<Task>(`${this._taskIdApi(taskId)}`, body, { context, params })
          : this._http.put<Task>(`${this._taskIdApi(taskId)}/send`, body, { context, params });
        }
        return throwError(() => new Error('OFFLINE'));
      })
    )
  }

  sendTask(projectId: string, taskId: number, formEntries: FormValue, isDraft: boolean, context?: HttpContext): Observable<Task> {

    return combineLatest({
      isOnline: this._connectionStatusService.networkStatus$,
      hasOfflineData: this._offlineService.hasOfflineData(taskId)
    }).pipe(
      take(1),
      switchMap(({isOnline, hasOfflineData}) => {
        if(!isOnline || hasOfflineData) {
          return this._offlineService.saveTaskData(projectId, taskId, formEntries, isDraft)
          .pipe(
            map(task => {
              if(task === null){
                throw new HttpErrorResponse({error: "Not found", status: HttpStatusCode.NotFound});
              }
              return task;
            })
          )
        } else {
          return this.saveTaskOnline(projectId, taskId, formEntries, isDraft, context);
        }
      }),
      withLatestFrom(this.tasks$),
      tap(([task, tasks]) => this.updateTasksInList([task], tasks)),
      map(([task]) => task)
    )
  }
  closeTask(taskId: number, payload: FormValue): Observable<Task> {
    const { blocked, ...body } = payload;
    return this._http.put<Task>(`${this._taskIdApi(taskId)}/close`, body, { params: this.getBlockedParams(blocked) });
  }

  // Brak parametru oznacza, że backend pozostawia zapisaną wartość bez zmian
  private getBlockedParams(blocked?: boolean): HttpParams {
    return blocked === undefined ? new HttpParams() : new HttpParams().set('blocked', blocked);
  }

  getForm(projectId: string, formId: number) {
    return this._http.get('/api/form/' + projectId + '/' + formId )
  }


  syncAll(projectId: string) {
    return this._offlineService.fetchTasks(projectId).pipe(
      concatMap(tasks => tasks), // Rozwijamy tablicę zadań na poszczególne zadania
      concatMap(task => this.syncIfOfflineDataExists(task.taskId)),
      finalize(() => {
        this.refreshTasks();
        this._storeService.hideLoader();
      })
    );
  }

  runSyncAllWithProgress(projectId: string): Observable<void> {
    return this.getSyncableTaskIds(projectId).pipe(
      switchMap(taskIds => this.runSyncWithProgress(taskIds, 'all'))
    );
  }

  runSyncTaskWithProgress(taskId: number): Observable<void> {
    return this.runSyncWithProgress([taskId], 'single');
  }

  requestStopSync(): void {
    this.syncProgressService.requestStop();
    this.stopSync$.next();
  }

  deleteOfflineTaskData(taskId: number): Observable<void> {
    return this.cleanupTaskData(taskId).pipe(
      map(() => void 0)
    );
  }

  private runSyncWithProgress(taskIds: number[], mode: SyncMode): Observable<void> {
    if (this.syncProgressService.snapshot.status === 'running') {
      return of(void 0);
    }

    this._connectionStatusService.setSyncStatus(true);
    this.syncProgressService.start(taskIds.length, mode);
    if (!taskIds.length) {
      this.syncProgressService.log('SYNC_PROGRESS.LOG_NO_TASKS');
      this.syncProgressService.finish('success');
      this._connectionStatusService.setSyncStatus(false);
      return of(void 0);
    }

    let failedTasks = 0;
    let lastFailureReason: { key: string, params?: Record<string, any> } | null = null;

    return from(taskIds).pipe(
      concatMap((taskId, index) => {
        if (this.syncProgressService.isStopRequested()) {
          return throwError(() => new Error('SYNC_STOPPED'));
        }

        this.syncProgressService.setCurrentTask(taskId, index + 1);
        this.syncProgressService.log('SYNC_PROGRESS.LOG_TASK_START', { taskId });
        return this.syncTaskWithAutoRetry(taskId).pipe(
          tap(() => {
            this.syncProgressService.incrementSyncedTasks();
            this.syncProgressService.setTaskResult(taskId, 'success');
            this.syncProgressService.log('SYNC_PROGRESS.LOG_TASK_SUCCESS', { taskId });
          }),
          catchError(error => {
            if (error?.message === 'SYNC_STOPPED') {
              return throwError(() => error);
            }
            failedTasks++;
            this.syncProgressService.incrementFailedTasks();
            const failureReason = this.classifySyncFailure(error);
            this.syncProgressService.setTaskResult(
              taskId,
              'failed',
              failureReason.key,
              failureReason.params,
              failureReason.photoIssues
            );
            lastFailureReason = failureReason;
            this.syncProgressService.log('SYNC_PROGRESS.LOG_TASK_FAILED', { taskId });
            return of(void 0);
          })
        );
      }),
      map(() => void 0),
      last(undefined, void 0),
      tap(() => {
        if (failedTasks > 0) {
          if (lastFailureReason) {
            this.syncProgressService.setFailureReason(lastFailureReason.key, lastFailureReason.params);
          }
          this.syncProgressService.log('SYNC_PROGRESS.LOG_INTERRUPTED');
          this.syncProgressService.finish('interrupted');
          return;
        }
        this.syncProgressService.finish('success');
      }),
      catchError(error => {
        if (error?.message === 'SYNC_STOPPED') {
          this.syncProgressService.finish('stopped');
          return of(void 0);
        }

        const failureReason = this.classifySyncFailure(error);
        this.syncProgressService.setFailureReason(failureReason.key, failureReason.params);
        this.syncProgressService.log('SYNC_PROGRESS.LOG_INTERRUPTED');
        this.syncProgressService.finish('interrupted');
        return of(void 0);
      }),
      finalize(() => this._connectionStatusService.setSyncStatus(false))
    );
  }

  private syncTaskWithAutoRetry(taskId: number): Observable<any> {
    if (this.activeSyncTaskIds.has(taskId)) {
      return of(void 0);
    }

    this.activeSyncTaskIds.add(taskId);
    return this.syncTaskWithAutoRetryAttempt(taskId, 1);
  }

  private syncTaskWithAutoRetryAttempt(taskId: number, attempt: number): Observable<any> {
    return this.withSyncCancellation(this.syncTaskWithProgress(taskId)).pipe(
      catchError(error => {
        if (this.syncProgressService.isStopRequested()) {
          return throwError(() => new Error('SYNC_STOPPED'));
        }

        if (!this.shouldRetrySyncError(error)) {
          return throwError(() => error);
        }

        if (attempt >= this.syncRetryMaxAttempts) {
          return throwError(() => error);
        }

        this.logSyncPauseReason(taskId, error, attempt);

        return this.withStopCancellation(this.getRetryWait$(error)).pipe(
          switchMap(() => {
            const nextAttempt = attempt + 1;
            this.syncProgressService.log(
              this.isOfflineSyncError(error)
                ? 'SYNC_PROGRESS.LOG_RESUME_TASK'
                : 'SYNC_PROGRESS.LOG_RETRY_TASK',
              {
              taskId,
              attempt: nextAttempt,
              maxAttempts: this.syncRetryMaxAttempts
              }
            );
            return this.syncTaskWithAutoRetryAttempt(taskId, nextAttempt);
          })
        );
      })
    ).pipe(
      finalize(() => this.activeSyncTaskIds.delete(taskId))
    );
  }

  private withStopCancellation<T>(source$: Observable<T>): Observable<T> {
    return race(source$, this.stopRequestedError$());
  }

  private withSyncCancellation<T>(source$: Observable<T>): Observable<T> {
    return this.withStopCancellation(this.withOfflineCancellation(source$));
  }

  private withOfflineCancellation<T>(source$: Observable<T>): Observable<T> {
    return race(source$, this.offlineError$());
  }

  private stopRequestedError$(): Observable<never> {
    return this.stopSync$.pipe(
      take(1),
      switchMap(() => throwError(() => new Error('SYNC_STOPPED')))
    );
  }

  private offlineError$(): Observable<never> {
    return this._connectionStatusService.networkStatus$.pipe(
      filter(isOnline => !isOnline),
      take(1),
      switchMap(() => throwError(() => new Error('OFFLINE')))
    );
  }

  private getRetryWait$(error: any): Observable<void> {
    if (this.isOfflineSyncError(error)) {
      return this._connectionStatusService.networkStatus$.pipe(
        filter(Boolean),
        take(1),
        map(() => void 0)
      );
    }

    return timer(this.syncRetryDelayMs).pipe(
      map(() => void 0)
    );
  }

  private shouldRetrySyncError(error: any): boolean {
    if (this.isOfflineSyncError(error) || this.isTimeoutSyncError(error)) {
      return true;
    }

    return this.isServerSyncError(error);
  }

  private isServerSyncError(error: any): boolean {
    return error instanceof HttpErrorResponse && error.status >= 500;
  }

  private isOfflineSyncError(error: any): boolean {
    if (error?.message === 'OFFLINE') {
      return true;
    }

    return error instanceof HttpErrorResponse && error.status === 0 && !this.isTimeoutSyncError(error);
  }

  private isTimeoutSyncError(error: any): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    const progressEventType = error.error?.type;
    const message = String(error.message || '').toLowerCase();
    return progressEventType === 'timeout' || message.includes('timeout');
  }

  private logSyncPauseReason(taskId: number, error: any, attempt: number): void {
    const currentPhotoIndex = this.syncProgressService.snapshot.currentPhotoIndex || 1;
    this.syncProgressService.setCurrentPhoto(currentPhotoIndex, 0);

    if (this.isOfflineSyncError(error)) {
      this.syncProgressService.log('SYNC_PROGRESS.LOG_PAUSED_OFFLINE', { taskId });
      return;
    }

    if (this.isTimeoutSyncError(error)) {
      this.syncProgressService.log('SYNC_PROGRESS.LOG_PAUSED_TIMEOUT', { taskId });
      return;
    }

    if (this.isServerSyncError(error)) {
      const status = (error as HttpErrorResponse).status;
      this.syncProgressService.log('SYNC_PROGRESS.LOG_PAUSED_SERVER', {
        taskId,
        status,
        attempt,
        maxAttempts: this.syncRetryMaxAttempts,
        retryInSeconds: Math.round(this.syncRetryDelayMs / 1000)
      });
    }
  }

  private syncIfOfflineDataExists(taskId: string): Observable<any> {
    return this._offlineService.hasOfflineData(taskId).pipe(
      concatMap(offlineData => {
        if (offlineData) {
          return this.syncTask(taskId);
        }
        return EMPTY; // Jeśli brak danych, zwracamy pusty strumień
      })
    );
  }
  syncTask(taskId: string | number | Task): Observable<any>{
    /*
      1. sprawdzić czy task jest offline
      2. przejrzeć każde entry czy zawiera usterki lub kontrolkę typu PHOTO i przejrzeć każdy załącznik:
        2.1 jeśli załącznik ma id < 0
          2.2 wysłać załącznik i podmienić w tasku dane załącznika odpowiedzią serwera
      3. wysłać taska
      4. usunąć lokalnie wszystkie dane taska i załączników do niego
    */
    const id = typeof(taskId) === 'number' || typeof(taskId) === 'string' ? taskId : taskId.id!;
    this._storeService.showLoader();
    return this._offlineService.fetchTaskData("", id)
    .pipe(
      switchMap(taskDataEntity => {
        if (!taskDataEntity) {
          return this.cleanupTaskData(Number(id)).pipe(
            switchMap(() => EMPTY),
            catchError(() => EMPTY)
          );
        }
        return this.ensureTaskSyncableOnline(Number(id)).pipe(
          switchMap(() => this.syncAllEntries(taskDataEntity))
        );
      }),
      switchMap(taskDataEntity => {
        if (this.hasPendingLocalPhotoIds(taskDataEntity.payload)) {
          return throwError(() => new Error('SYNC.ATTACHMENTS_INCOMPLETE'));
        }
        return of(taskDataEntity);
      }),
      tap(() => this._storeService.loaderText = this._translateService.instant('SYNC.SAVING_TASK')),
      switchMap(taskDataEntity =>
        this.saveTaskOnline(taskDataEntity.projectId, Number(id), taskDataEntity.payload, taskDataEntity.isDraft)
        .pipe(
          // Konieczne, aby uniknąć podwójnego komunikatu o ew. błędzie
          catchError(error => {
            throw(new Error())
          })
        )
      ),
      withLatestFrom(this.tasks$),
      map(([task, tasks]) => {
        this.updateTasksInList([task], tasks);
        return task;
      }),
      tap(() => this._storeService.loaderText = this._translateService.instant('SYNC.CLEANING')),
      switchMap(task => this.cleanupTaskData(task.id)),
      finalize(() => {
        this._storeService.loaderText = "";
      }),
      catchError(error => {
        error.message && this._storeService.showErrorMsg(error.message);
        return of(undefined);
      })
    )
  }

  private syncTaskWithProgress(taskId: string | number | Task): Observable<any> {
    const id = Number(typeof taskId === 'number' || typeof taskId === 'string' ? taskId : taskId.id);
    return this._offlineService.fetchTaskData("", id).pipe(
      switchMap(taskDataEntity => {
        if (this.syncProgressService.isStopRequested()) {
          return throwError(() => new Error('SYNC_STOPPED'));
        }

        if (!taskDataEntity) {
          this.syncProgressService.log('SYNC_PROGRESS.LOG_MISSING_DATA', { taskId: id });
          return this.cleanupTaskData(id).pipe(
            switchMap(() => EMPTY),
            catchError(() => EMPTY)
          );
        }
        return this.withSyncCancellation(
          this.ensureTaskSyncableOnline(id)
        ).pipe(
          switchMap(() => this.getTaskFormEntries(taskDataEntity).pipe(
            switchMap(formEntries => this.syncAllEntriesWithProgress(taskDataEntity, formEntries))
          ))
        );
      }),
      switchMap(taskDataEntity => {
        if (this.syncProgressService.isStopRequested()) {
          return throwError(() => new Error('SYNC_STOPPED'));
        }
        if (this.hasPendingLocalPhotoIds(taskDataEntity.payload)) {
          const candidates = this.collectPhotoSyncCandidates(taskDataEntity.payload, []);
          return throwError(() => new AttachmentSyncError(
            candidates
              .filter(candidate => (candidate.photo.id ?? 0) < 0)
              .map(candidate => this.toPhotoIssue(candidate, 'UPLOAD_NOT_CONFIRMED'))
          ));
        }
        return of(taskDataEntity);
      }),
      tap(() => this.syncProgressService.setStage('saving')),
      tap(() => this.syncProgressService.log('SYNC_PROGRESS.LOG_SAVING_TASK', { taskId: id })),
      switchMap(taskDataEntity =>
        this.withSyncCancellation(
          this.saveTaskOnline(taskDataEntity.projectId, id, taskDataEntity.payload, taskDataEntity.isDraft)
        )
      ),
      withLatestFrom(this.tasks$),
      map(([task, tasks]) => {
        this.updateTasksInList([task], tasks);
        return task;
      }),
      tap(() => this.syncProgressService.setStage('cleaning')),
      tap(() => this.syncProgressService.log('SYNC_PROGRESS.LOG_CLEANING', { taskId: id })),
      switchMap(task => this.cleanupTaskData(task.id))
    );
  }

  private syncAllEntriesWithProgress(taskDataEntity: TaskDataEntity, formEntries: FormEntry[]): Observable<TaskDataEntity> {
    const candidates = this.collectPhotoSyncCandidates(taskDataEntity.payload, formEntries);
    const entryObservables = candidates.map(candidate => this.syncPhotoCandidate(candidate));
    const total = entryObservables.length;
    this.syncProgressService.setStage('uploading');
    this.syncProgressService.setCurrentItemProgress(0, total);

    if (!total) {
      this.syncProgressService.log('SYNC_PROGRESS.LOG_NO_ATTACHMENTS', { taskId: taskDataEntity.taskId });
      return of(taskDataEntity);
    }

    let uploaded = 0;
    return this.validatePhotoCandidates(candidates).pipe(
      switchMap(() => from(entryObservables).pipe(
        concatMap((entryObservable, index) => {
          const current = index + 1;
          this.syncProgressService.setCurrentPhoto(current, 0);
          this.syncProgressService.log('SYNC_PROGRESS.LOG_UPLOADING_ITEM_START', {
            taskId: taskDataEntity.taskId,
            done: current,
            total
          });
          return this.withSyncCancellation(entryObservable).pipe(
            catchError(error => {
              const details = this.getSyncErrorDetails(error);
              this.syncProgressService.log('SYNC_PROGRESS.LOG_UPLOADING_ITEM_FAILED', {
                taskId: taskDataEntity.taskId,
                done: current,
                total,
                status: details.status,
                code: details.code,
                message: details.message
              });
              return throwError(() => error);
            })
          );
        }),
        tap(() => {
          uploaded++;
          this.syncProgressService.setCurrentPhoto(uploaded, 100);
          this.syncProgressService.setCurrentItemProgress(uploaded, total);
          this.syncProgressService.log('SYNC_PROGRESS.LOG_UPLOADING_ITEM', {
            taskId: taskDataEntity.taskId,
            done: uploaded,
            total
          });
        }),
        switchMap(attachment => {
          if (this.syncProgressService.isStopRequested()) {
            return throwError(() => new Error('SYNC_STOPPED'));
          }
          return of(attachment);
        }),
        filter(attachment => !!attachment),
        switchMap(() => this._offlineService.saveTaskData(
          taskDataEntity.projectId,
          String(taskDataEntity.taskId),
          taskDataEntity.payload,
          taskDataEntity.isDraft
        )),
        defaultIfEmpty(void 0),
        last(),
        map(() => taskDataEntity)
      ))
    );
  }

  private getSyncableTaskIds(projectId: string): Observable<number[]> {
    return this._offlineService.fetchTasks(projectId).pipe(
      switchMap(tasks => {
        if (!tasks.length) {
          return of([] as number[]);
        }
        return forkJoin(
          tasks.map(task => this._offlineService.hasOfflineData(task.taskId).pipe(
            map(hasOfflineData => hasOfflineData ? Number(task.taskId) : null)
          ))
        ).pipe(
          map(ids => ids.filter((id): id is number => id !== null))
        );
      })
    );
  }

  private classifySyncFailure(error: any): {
    key: string,
    params?: Record<string, any>,
    photoIssues?: SyncPhotoIssue[]
  } {
    if (error?.message === 'OFFLINE') {
      return { key: 'SYNC_PROGRESS.FAILURE_REASON_NETWORK' };
    }

    if (error instanceof AttachmentSyncError) {
      return {
        key: 'SYNC_PROGRESS.FAILURE_REASON_ATTACHMENTS',
        photoIssues: error.photoIssues
      };
    }

    if (error?.message === 'SYNC.ATTACHMENTS_INCOMPLETE') {
      return { key: 'SYNC_PROGRESS.FAILURE_REASON_ATTACHMENTS' };
    }

    if (error instanceof HttpErrorResponse) {
      if (error.error?.code === 'TASK_STATUS_NOT_SYNCABLE') {
        return {
          key: 'SYNC_PROGRESS.FAILURE_REASON_TASK_STATUS',
          params: {
            taskStatus: error.error?.taskStatus
          }
        };
      }
      if (error.status === 0) {
        return { key: 'SYNC_PROGRESS.FAILURE_REASON_NETWORK' };
      }
      if (error.status >= 500) {
        return { key: 'SYNC_PROGRESS.FAILURE_REASON_SERVER', params: { status: error.status } };
      }
      if (error.status >= 400) {
        return {
          key: 'SYNC_PROGRESS.FAILURE_REASON_CLIENT',
          params: {
            status: error.status,
            code: error.error?.code,
            message: error.error?.message
          }
        };
      }
    }

    return { key: 'SYNC_PROGRESS.FAILURE_REASON_UNKNOWN' };
  }

  private syncAllEntries(taskDataEntity: TaskDataEntity): Observable<TaskDataEntity> {
    const entryObservables = this.syncEntries(taskDataEntity?.payload);

    if(!entryObservables.length) {
      return of(taskDataEntity);
    }
    let uploaded = 0;
    return from(entryObservables) //upload
    .pipe(
      concatAll(),
      tap(() => this._storeService.loaderText = this._translateService.instant('SYNC.UPLOADING_ATTACHMENTS', { progress: `${++uploaded} / ${entryObservables.length}`})),
      filter(attachment => !!attachment),
      // Zapis taska po uploadzie każdego załącznika! Jeśli coś pójdzie nie tak, to nie tracimy tego co już zostało wysłane
      switchMap(_ => {
        return this._offlineService.saveTaskData(taskDataEntity.projectId, String(taskDataEntity.taskId), taskDataEntity.payload, taskDataEntity.isDraft)
      }),
      //Czekamy aż wyślą się wszystkie załączniki
      defaultIfEmpty(void 0), //zabezpieczenie dla operatora last(), który się wywala jeśli strumień nie zwróci wartości
      last(),
      map(() => taskDataEntity)
    )
  }

  private syncEntries(entries: FormValue): Observable<Attachment[] | null>[] {
    return entries.formEntries.flatMap(entry => this.syncEntry(entry));
  }

  private syncEntry(entry: FormEntryValue): Observable<Attachment[] | null>[] {
    const photos = entry.failureDescription?.flatMap(failure => this.syncPhotos(failure.photos)) ?? [];
    const values = entry.values?.flatMap(value => this.syncPhotos(this.getPhotoAttachments(value.value))) ?? [];
    const childPhotos = entry.children?.flatMap(child => this.syncEntry(child)) || [];
    return photos.concat(values, childPhotos);
  }

  private getTaskFormEntries(taskDataEntity: TaskDataEntity): Observable<FormEntry[]> {
    return this._offlineService.fetchTask(taskDataEntity.projectId, taskDataEntity.taskId).pipe(
      map(taskEntity => taskEntity?.payload.form?.entries ?? []),
      catchError(() => of([]))
    );
  }

  private collectPhotoSyncCandidates(
    entries: FormValue,
    formEntries: FormEntry[]
  ): PhotoSyncCandidate[] {
    const collectEntry = (
      entry: FormEntryValue,
      schemas: FormEntry[],
      parentPath: string[]
    ): PhotoSyncCandidate[] => {
      const schema = schemas.find(candidate =>
        candidate.templateFormEntryId === entry.id &&
        (candidate.cloneIndex ?? -1) === (entry.cloneIndex ?? -1)
      );
      const entryLabel = this.getEntryDiagnosticLabel(schema, entry);
      const entryPath = [...parentPath, entryLabel];

      const failurePhotos = entry.failureDescription?.flatMap(failure =>
        (failure.photos ?? [])
          .filter(photo => (photo.id ?? 0) < 0)
          .map(photo => ({
            photo,
            entryId: entry.id,
            cloneIndex: entry.cloneIndex,
            entryPath,
            source: 'FAILURE' as const,
            failureName: failure.name
          }))
      ) ?? [];

      const controlPhotos = entry.values?.flatMap(value => {
        const photos = this.getPhotoAttachments(value.value);
        if (!photos.length) {
          return [];
        }
        const control = schema?.control?.find(candidate => candidate.templateFormControlId === value.id);
        return photos
          .filter(photo => (photo.id ?? 0) < 0)
          .map(photo => ({
            photo,
            entryId: entry.id,
            cloneIndex: entry.cloneIndex,
            entryPath,
            source: 'PHOTO_CONTROL' as const,
            controlId: value.id,
            controlLabel: control?.label || control?.name
          }));
      }) ?? [];

      const childPhotos = entry.children?.flatMap(child =>
        collectEntry(child, schema?.children ?? [], entryPath)
      ) ?? [];

      return [...failurePhotos, ...controlPhotos, ...childPhotos];
    };

    return entries.formEntries.flatMap(entry => collectEntry(entry, formEntries, []));
  }

  private getEntryDiagnosticLabel(schema: FormEntry | undefined, entry: FormEntryValue): string {
    if (!schema) {
      return `ID ${entry.id}`;
    }
    const name = schema.displayName || schema.name || `ID ${entry.id}`;
    if (!schema.customValue) {
      return name;
    }
    const custom = schema.customLabel
      ? `${schema.customLabel}: ${schema.customValue}`
      : schema.customValue;
    return `${name} (${custom})`;
  }

  private syncPhotoCandidate(candidate: PhotoSyncCandidate): Observable<Attachment[] | null> {
    const localAttachmentId = -(candidate.photo.id!);
    return this._offlineService.fetchAttachment(localAttachmentId).pipe(
      switchMap(entity => {
        const validationReason = this.validateLocalAttachment(entity);
        if (validationReason) {
          return throwError(() => new AttachmentSyncError([
            this.toPhotoIssue(candidate, validationReason)
          ]));
        }

        const formData = new FormData();
        entity!.payload.forEach(([key, value]) => formData.append(key, value));
        return this._attachmentService.uploadOnline(formData, 'PHOTO', 0);
      }),
      tap(resp => {
        if (resp?.type !== HttpEventType.UploadProgress) {
          return;
        }
        const progressEvent = resp as HttpProgressEvent;
        if (!progressEvent.total) {
          return;
        }
        const percent = Math.round((100 * progressEvent.loaded) / progressEvent.total);
        const currentPhotoIndex = this.syncProgressService.snapshot.currentPhotoIndex || 1;
        this.syncProgressService.setCurrentPhoto(currentPhotoIndex, percent);
      }),
      filter(resp => resp?.type === HttpEventType.Response),
      switchMap(resp => {
        const body = (resp as HttpResponse<Attachment[]>).body;
        const serverId = body?.[0]?.id;
        if (!body?.length || typeof serverId !== 'number' || serverId <= 0) {
          return throwError(() => new AttachmentSyncError([
            this.toPhotoIssue(candidate, 'SERVER_RESPONSE_INVALID')
          ]));
        }
        candidate.photo.id = serverId;
        return of(body);
      }),
      defaultIfEmpty(null),
      switchMap(result => result === null
        ? throwError(() => new AttachmentSyncError([
            this.toPhotoIssue(candidate, 'SERVER_RESPONSE_INVALID')
          ]))
        : of(result)
      )
    );
  }

  private validatePhotoCandidates(candidates: PhotoSyncCandidate[]): Observable<void> {
    if (!candidates.length) {
      return of(void 0);
    }
    return forkJoin(candidates.map(candidate => {
      const localAttachmentId = -(candidate.photo.id!);
      return this._offlineService.fetchAttachment(localAttachmentId).pipe(
        map(entity => {
          const reason = this.validateLocalAttachment(entity);
          return reason ? this.toPhotoIssue(candidate, reason) : null;
        }),
        catchError(() => of(this.toPhotoIssue(candidate, 'LOCAL_RECORD_MISSING')))
      );
    })).pipe(
      map(issues => issues.filter((issue): issue is SyncPhotoIssue => issue !== null)),
      switchMap(issues => issues.length
        ? throwError(() => new AttachmentSyncError(issues))
        : of(void 0)
      )
    );
  }

  private validateLocalAttachment(entity?: AttachmentEntity): SyncPhotoIssueReason | null {
    if (!entity) {
      return 'LOCAL_RECORD_MISSING';
    }
    const file = entity.payload?.find(([key]) => key === 'files')?.[1];
    if (!(file instanceof Blob)) {
      return 'FILE_PAYLOAD_MISSING';
    }
    return null;
  }

  private toPhotoIssue(candidate: PhotoSyncCandidate, reason: SyncPhotoIssueReason): SyncPhotoIssue {
    return {
      localId: candidate.photo.id!,
      originalFilename: candidate.photo.originalFilename || '—',
      thumbnail: candidate.photo.thumbnail,
      entryId: candidate.entryId,
      cloneIndex: candidate.cloneIndex,
      entryPath: candidate.entryPath,
      source: candidate.source,
      failureName: candidate.failureName,
      controlId: candidate.controlId,
      controlLabel: candidate.controlLabel,
      reason
    };
  }


  private getPhotoAttachments(value: unknown): PhotoAttachment[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.filter((item): item is PhotoAttachment =>
      !!item &&
      typeof item === 'object' &&
      !Array.isArray(item) &&
      item.type === 'PHOTO'
    );
  }

  private syncPhotos(photos: PhotoAttachment[]): Observable<Attachment[] | null>[] {
    return photos
    .filter(photo => (photo.id ?? 0) < 0)
    .map(photo => {
        const localAttachmentId = -photo.id!;
        return this._offlineService.getAttachmentFormData(-photo.id!)
        .pipe(
          switchMap(attachment => {
            if (!attachment) {
              return this._offlineService.deleteAttachment(localAttachmentId).pipe(
                map(() => null)
              );
            }
            return this._attachmentService.uploadOnline(attachment, 'PHOTO', 0);
          }),
          tap(resp => {
            if (resp && resp.type === HttpEventType.UploadProgress) {
              const progressEvent = resp as HttpProgressEvent;
              if (!progressEvent.total) {
                return;
              }
              const percent = Math.round((100 * progressEvent.loaded) / progressEvent.total);
              const currentPhotoIndex = this.syncProgressService.snapshot.currentPhotoIndex || 1;
              this.syncProgressService.setCurrentPhoto(currentPhotoIndex, percent);
            }
          }),
          filter(resp => !!resp && (resp.type == HttpEventType.Response)),
          filter(resp => !!((resp as HttpResponse<Attachment[]>).body)),
          map(resp => (resp as HttpResponse<Attachment[]>).body!),
          tap(body => photo.id = body![0].id),
          defaultIfEmpty(null)
        );
    })
  }

  private cleanupTaskData(taskId: number): Observable<[boolean[], boolean, boolean]> {
    return forkJoin([
      this._offlineService.deleteTaskAttachments(taskId).pipe(
        catchError(() => of([false]))
      ),
      this._offlineService.deleteTaskData(taskId).pipe(
        catchError(() => of(false))
      ),
      this._offlineService.deleteTask(taskId).pipe(
        catchError(() => of(false))
      )
    ]);
  }

  private hasPendingLocalPhotoIds(entries: FormValue): boolean {
    const hasPendingInEntry = (entry: FormEntryValue): boolean => {
      const failurePhotosPending = entry.failureDescription?.some(
        failure => failure.photos?.some(photo => (photo.id ?? 0) < 0)
      ) ?? false;
      const controlPhotosPending = entry.values?.some(
        value => this.getPhotoAttachments(value.value).some(photo => (photo.id ?? 0) < 0)
      ) ?? false;
      const childPending = entry.children?.some(child => hasPendingInEntry(child)) ?? false;
      return failurePhotosPending || controlPhotosPending || childPending;
    };

    return entries.formEntries.some(entry => hasPendingInEntry(entry));
  }

  private getSyncErrorDetails(error: any): { status?: number; code?: string; message?: string } {
    if (!(error instanceof HttpErrorResponse)) {
      return { message: error?.message };
    }

    const payload = error.error;
    const code = payload?.code || undefined;
    const message =
      payload?.message ||
      (typeof payload === 'string' ? payload : undefined) ||
      error.message ||
      undefined;

    return {
      status: error.status,
      code,
      message
    };
  }

  private ensureTaskSyncableOnline(taskId: number): Observable<void> {
    return this.getOnlineTaskStatus(taskId).pipe(
      take(1),
      switchMap(status => {
        if (this.syncableOnlineTaskStatuses.includes(status)) {
          return of(void 0);
        }

        if (status === 'CANCELLED') {
          return throwError(() => new HttpErrorResponse({
            status: HttpStatusCode.Conflict,
            error: {
              code: 'TASK_STATUS_CANCELLED',
              message: 'SYNC_PROGRESS.FAILURE_REASON_CANCELLED',
              taskStatus: status
            }
          }));
        }

        return throwError(() => new HttpErrorResponse({
          status: HttpStatusCode.Conflict,
          error: {
            code: 'TASK_STATUS_NOT_SYNCABLE',
            message: 'SYNC_PROGRESS.FAILURE_REASON_TASK_STATUS',
            taskStatus: status
          }
        }));
      })
    );
  }

  private getTasksParams(withForm: boolean = false): HttpParams {
    return new HttpParams().set('withFormData', this.isMobile && withForm);
  }

  reopen(taskIds: number[]): Observable<TaskStatusChangeResponse> {
    return this._http.put<TaskStatusChangeResponse>(this._tasksApi() + '/reopen', taskIds)
  }

  revoke(taskIds: number[]): Observable<TaskStatusChangeResponse> {
    return this._http.put<TaskStatusChangeResponse>(this._tasksApi() + '/revoke', taskIds)
  }

  multiApprove(taskIds: number[]): Observable<TaskMultiAcceptResponse> {
    return this._http.put<TaskMultiAcceptResponse>(this._tasksApi() + '/close', taskIds)
  }

  refreshPseStatus(taskId: number): Observable<Task> {
    return this._http.post<Task>(this._taskIdApi(taskId) + '/refresh-pse-status', {}).pipe(
      withLatestFrom(this.tasks$),
      tap(([task, currentTasks]) => {
        if (isPseToCorrect(task)) {
          this._tasks$.next(currentTasks.filter(t => t.id !== task.id));
        } else {
          this.updateTasksInList([task], currentTasks);
        }
      }),
      map(([task]) => task)
    );
  }

  pushToPse(taskId: number): Observable<Task> {
    // Błędy i loader obsługuje wywołujący: wysyłka trwa dalej, a na końcu pokazuje zbiorczy komunikat
    const context = new HttpContext()
      .set(SKIP_ERROR_TOAST, true)
      .set(SKIP_LOADER_HIDE, true);
    return this._http.post<Task>(this._taskIdApi(taskId) + '/push-to-pse', {}, { context }).pipe(
      withLatestFrom(this.tasks$),
      tap(([task, currentTasks]) => this.updateTasksInList([task], currentTasks)),
      map(([task]) => task)
    );
  }

  getSimilarTasks(taskId: number): Observable<Task[]> {
    return this._http.get<Task[]>(`${this._tasksApi()}/similar-to/${taskId}`)
  }

  copyTasks(taskId: number, targetTaskIds: number[]): Observable<TaskCopyError[]> {
    return this._http.put<TaskCopyError[]>(this._taskIdApi(taskId) + '/copy',  targetTaskIds);
  }

  assignTasks(taskIds: number[], userId: string | null): Observable<TaskAssignResponse> {
    return this._http.put<TaskAssignResponse>(this._tasksApi() + '/assign', taskIds);
  }

  assignTasksToUser(taskIds: number[], userId: string | null): Observable<TaskAssignResponse> {
    return this._http.put<TaskAssignResponse>(this._tasksApi() + '/assign-to', {
      taskIds,
      userId
    });
  }

  unassignTasks(taskIds: number[]): Observable<TaskAssignResponse> {
    return this._http.put<TaskAssignResponse>(this._tasksApi() + '/unassign', taskIds);
  }

  getOnlineTaskStatus(taskId: number): Observable<TaskStatus> {
    return this._http.get<TaskStatus>(this._tasksApi() + `/${taskId}/status`);
  }
}
